import { useEffect, useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';
import { DollarSign, Eye, MousePointer, TrendingUp, Play, Image, FileText, Filter } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#8B5CF6', '#EC4899', '#3B82F6', '#10B981', '#F59E0B', '#EF4444'];

export const AdPerformance = () => {
  const [adData, setAdData] = useState(null);
  const [selectedType, setSelectedType] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/analytics/ads`);
        setAdData(response.data);
      } catch (error) {
        console.error('Error fetching ad data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredContent = adData?.boosted_content?.filter(
    item => selectedType === 'all' || item.type === selectedType
  ) || [];

  const typeData = adData?.boosted_content?.reduce((acc, item) => {
    const existing = acc.find(x => x.type === item.type);
    if (existing) {
      existing.spend += item.spend;
      existing.views += item.views;
    } else {
      acc.push({ type: item.type, spend: item.spend, views: item.views });
    }
    return acc;
  }, []) || [];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64" data-testid="loading">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="ad-performance-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Ad Performance</h1>
            <p className="text-slate-500 mt-1">Boosted content & campaign analytics from GiriUK reports</p>
          </div>
          <Badge variant="outline" className="text-purple-600 border-purple-200">Oct 2025 - Jan 2026</Badge>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-white shadow-sm card-hover" data-testid="kpi-total-spend">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <DollarSign className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Spend</p>
                  <p className="text-2xl font-bold text-slate-800">£{adData?.summary?.total_spend?.toFixed(2)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-sm card-hover" data-testid="kpi-total-views">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Eye className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Views</p>
                  <p className="text-2xl font-bold text-slate-800">{adData?.summary?.total_views?.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-sm card-hover" data-testid="kpi-total-clicks">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-pink-100 rounded-lg">
                  <MousePointer className="h-5 w-5 text-pink-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Clicks</p>
                  <p className="text-2xl font-bold text-slate-800">{adData?.summary?.total_clicks?.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-sm card-hover" data-testid="kpi-avg-ctr">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Avg CTR</p>
                  <p className="text-2xl font-bold text-slate-800">{adData?.summary?.avg_ctr?.toFixed(2)}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Performance by Content */}
          <Card className="bg-white shadow-sm" data-testid="performance-chart">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-800">Performance by Content</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={filteredContent} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={60} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                      formatter={(value, name) => [value.toLocaleString(), name === 'views' ? 'Views' : name === 'clicks' ? 'Clicks' : name]}
                    />
                    <Bar dataKey="views" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="clicks" fill="#EC4899" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Spend by Type */}
          <Card className="bg-white shadow-sm" data-testid="spend-chart">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-800">Spend Distribution by Type</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={typeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="spend"
                      nameKey="type"
                      label={({ type, percent }) => `${type} ${(percent * 100).toFixed(0)}%`}
                    >
                      {typeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`£${value.toFixed(2)}`, 'Spend']} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Content Type Filter */}
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-500" />
          <span className="text-sm text-slate-500">Filter by type:</span>
          {['all', 'reel', 'post', 'campaign'].map((type) => (
            <Button
              key={type}
              variant={selectedType === type ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedType(type)}
              className={selectedType === type ? 'gradient-btn text-white' : ''}
              data-testid={`filter-${type}`}
            >
              {type === 'all' ? 'All' : type.charAt(0).toUpperCase() + type.slice(1)}
            </Button>
          ))}
        </div>

        {/* Boosted Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="boosted-content-grid">
          {filteredContent.map((item) => (
            <Card key={item.id} className="bg-white shadow-sm overflow-hidden group hover:shadow-lg transition-all cursor-pointer">
              <div className="relative aspect-video overflow-hidden">
                <img 
                  src={item.thumbnail} 
                  alt={item.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-2 left-2">
                  <Badge className={`${item.type === 'reel' ? 'bg-purple-500' : item.type === 'campaign' ? 'bg-pink-500' : 'bg-blue-500'} text-white`}>
                    {item.type === 'reel' && <Play className="h-3 w-3 mr-1" />}
                    {item.type === 'post' && <Image className="h-3 w-3 mr-1" />}
                    {item.type === 'campaign' && <FileText className="h-3 w-3 mr-1" />}
                    {item.type}
                  </Badge>
                </div>
                <div className="absolute top-2 right-2">
                  <Badge variant="secondary" className="bg-white/90 text-slate-700">
                    {item.month}
                  </Badge>
                </div>
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold text-slate-800 truncate mb-3">{item.name}</h3>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Views</p>
                    <p className="text-sm font-bold text-slate-800">{item.views.toLocaleString()}</p>
                  </div>
                  <div className="p-2 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">CTR</p>
                    <p className="text-sm font-bold text-purple-600">{item.ctr}%</p>
                  </div>
                  <div className="p-2 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Spend</p>
                    <p className="text-sm font-bold text-slate-800">£{item.spend}</p>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs text-slate-500">Reach: {item.reach.toLocaleString()}</span>
                  <Badge variant="outline" className="text-emerald-600 border-emerald-200 text-xs">
                    {item.status}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default AdPerformance;
